<script setup>
import { useCampaignGift } from "@/composables/campaign-gift";
import { useCampaignGiftClaim } from "@/composables/campaign-gift-claim";
import { useCampaignGiftBonusPoint } from "@/composables/campaign-gift-bonus-point";
import { computed, getCurrentInstance, onMounted, reactive, ref, watch } from "vue";
import CampaignGiftModal from "./campaignGiftModal.vue";
import { notificationsMixin } from "@/mixins";
import CampaignGiftsClaimModal from "./campaignGiftsClaimModal.vue";
import MsgBoxConfirm from "@/components/msg-box-confirm.vue";
import MultiselectSearch from "@/components/multiselect-search.vue";
import Setting from "@/views/campaigns/campaign-settings/setting.vue";
import ChineseAuctionSchema from "@/views/campaigns/campaign-settings/settings-schemas/chinese_auction.schema";
import {
  CharidyCampaignGiftsClaimStatusCanceled,
  CharidyCampaignGiftsClaimStatusCurrentlyShipping,
  CharidyCampaignGiftsClaimStatusDelivered,
  CharidyCampaignGiftsClaimStatusOther,
  CharidyCampaignGiftsClaimStatusProcessed,
  CharidyCampaignGiftsClaimStatusSentToCarrier,
} from "@/composables/campaign-gift-claim";
import urls, { appHost } from "@/constants/urls.js";

const {
  list: campaignGiftList,
  total: campaignGiftListTotal,
  loading: loadingCampaignGift,
  fetchOrgCampaignGiftList,
  deleteOrgCampaignGift,
} = useCampaignGift();
const {
  items: campaignGiftsClaimList,
  total: campaignGiftsClaimListTotal,
  loading: loadingCampaignGiftsClaim,
  fetchOrgCampaignGiftClaimListByOrg,
  deleteOrgCampaignGiftClaim,
  bulkUpdateOrgCampaignGiftClaimStatus,
  exportCampaignGiftClaimByOrg,
} = useCampaignGiftClaim();
const {
  items: campaignGiftBonusPointList,
  total: campaignGiftBonusPointListTotal,
  loading: loadingCampaignGiftBonusPoint,
  submitting: submittingCampaignGiftBonusPoint,
  fetchOrgCampaignGiftBonusPointList,
  awardOrgCampaignGiftBonusPoint,
  updateOrgCampaignGiftBonusPoint,
  deleteOrgCampaignGiftBonusPoint,
} = useCampaignGiftBonusPoint();

const vm = getCurrentInstance();
const $t = vm.proxy.$t.bind(vm.proxy);
const cId = vm.proxy.$store.state.campaign.campaign.data.id;
const orgId = localStorage.getItem("orgId");
const notificationsMixinHandleCatch =
  notificationsMixin.methods.$_notificationsMixin_handleCatch.bind(vm.proxy);
const notificationsMixinMakeToastCompat =
  notificationsMixin.methods.$_notificationsMixin_makeToastCompat.bind(vm.proxy);

const campaignGiftListPage = ref(1);
const campaignGiftsClaimListPage = ref(1);
const campaignGiftBonusPointListPage = ref(1);
const campaignGiftBonusPointFilterTeam = ref(null);

// Gift-claim list filters
const claimFilterStatus = ref("");
const claimFilterGiftName = ref("");
const claimFilterTeamName = ref("");
const claimFilterPoints = ref("");

const teamSearchUrl = computed(() =>
  urls.apiTeams.replace(":orgId", orgId).replace(":campaignId", cId)
);

const teamSearchOptions = {
  placeholder: "Select team",
  changeParams: ({ query, page, per_page }) => ({ q: query, page, limit: per_page }),
  customLabel: (e) => `[${e.id}] ${e.name}`,
  processResults: (data) => data.map((el) => ({ id: el.id, ...el.attributes })),
};

const bonusPointForm = reactive({
  team: null,
  points: "",
  comment: "",
});

const bonusPointEditModalVisible = ref(false);
const bonusPointEditForm = reactive({
  id: null,
  points: "",
  comment: "",
});

function openEditBonusPoint(item) {
  bonusPointEditForm.id = item.ID;
  bonusPointEditForm.points = item.points;
  bonusPointEditForm.comment = item.comment || "";
  bonusPointEditModalVisible.value = true;
}

async function onSubmitEditBonusPoint() {
  const points = Number(bonusPointEditForm.points);
  if (!points || Number.isNaN(points)) {
    notificationsMixinMakeToastCompat({
      group: "ErrorNotification",
      title: $t(
        "edit_campaign.campaign_gifts_internal_tab_bonus_points_points_required",
        "Please enter points"
      ),
    });
    return;
  }

  try {
    await updateOrgCampaignGiftBonusPoint(cId, bonusPointEditForm.id, {
      points,
      comment: bonusPointEditForm.comment || "",
    });

    notificationsMixinMakeToastCompat({
      group: "Notification",
      title: $t(
        "edit_campaign.campaign_gifts_internal_tab_bonus_points_update_success",
        "Bonus points updated"
      ),
    });

    bonusPointEditModalVisible.value = false;
    refetchCampaignGiftBonusPointList();
  } catch (e) {
    notificationsMixinHandleCatch(e);
  }
}

async function onDeleteBonusPoint(id) {
  try {
    await deleteOrgCampaignGiftBonusPoint(cId, id);
    notificationsMixinMakeToastCompat({
      group: "Notification",
      title: $t(
        "edit_campaign.campaign_gifts_internal_tab_bonus_points_delete_success",
        "Bonus points deleted"
      ),
    });
    refetchCampaignGiftBonusPointList();
  } catch (e) {
    notificationsMixinHandleCatch(e);
  }
}

function bonusPointListParams(page) {
  const params = {
    page,
    per_page: 10,
  };
  if (campaignGiftBonusPointFilterTeam.value?.id) {
    params.team_id = Number(campaignGiftBonusPointFilterTeam.value.id);
  }
  return params;
}

function refetchCampaignGiftBonusPointList() {
  fetchOrgCampaignGiftBonusPointList(
    cId,
    bonusPointListParams(campaignGiftBonusPointListPage.value)
  );
}

function claimListParams(page) {
  const params = {
    page,
    per_page: 10,
  };

  if (claimFilterStatus.value) {
    params.status = claimFilterStatus.value;
  }
  if (claimFilterGiftName.value?.trim()) {
    params.gift_name = claimFilterGiftName.value.trim();
  }
  if (claimFilterTeamName.value?.trim()) {
    params.team_name = claimFilterTeamName.value.trim();
  }
  if (claimFilterPoints.value !== "" && claimFilterPoints.value !== null) {
    const points = Number(claimFilterPoints.value);
    if (!Number.isNaN(points)) {
      params.points = points;
    }
  }

  return params;
}

function refetchCampaignGiftClaimList() {
  selectedClaimIds.value = [];
  fetchOrgCampaignGiftClaimListByOrg(cId, claimListParams(campaignGiftsClaimListPage.value));
}

function applyClaimFilters() {
  // Reset to first page so filtered results start from the top.
  // Changing the page triggers its watcher (which refetches); when already
  // on page 1 we refetch directly.
  if (campaignGiftsClaimListPage.value !== 1) {
    campaignGiftsClaimListPage.value = 1;
  } else {
    refetchCampaignGiftClaimList();
  }
}

function resetClaimFilters() {
  claimFilterStatus.value = "";
  claimFilterGiftName.value = "";
  claimFilterTeamName.value = "";
  claimFilterPoints.value = "";
  applyClaimFilters();
}

onMounted(() => {
  fetchOrgCampaignGiftList(cId, {
    page: campaignGiftListPage.value,
    per_page: 10,
  });
  refetchCampaignGiftClaimList();
  refetchCampaignGiftBonusPointList();
});

watch(campaignGiftListPage, (v) => {
  fetchOrgCampaignGiftList(cId, {
    page: v,
    per_page: 10,
  });
});

watch(campaignGiftsClaimListPage, () => {
  refetchCampaignGiftClaimList();
});

watch(campaignGiftBonusPointListPage, () => {
  refetchCampaignGiftBonusPointList();
});

watch(campaignGiftBonusPointFilterTeam, () => {
  if (campaignGiftBonusPointListPage.value !== 1) {
    campaignGiftBonusPointListPage.value = 1;
  } else {
    refetchCampaignGiftBonusPointList();
  }
});

async function onSubmitAwardBonusPoint() {
  const teamId = bonusPointForm.team?.id;
  if (!teamId) {
    notificationsMixinMakeToastCompat({
      group: "ErrorNotification",
      title: $t(
        "edit_campaign.campaign_gifts_internal_tab_bonus_points_team_required",
        "Please select a team"
      ),
    });
    return;
  }

  const points = Number(bonusPointForm.points);
  if (!points || Number.isNaN(points)) {
    notificationsMixinMakeToastCompat({
      group: "ErrorNotification",
      title: $t(
        "edit_campaign.campaign_gifts_internal_tab_bonus_points_points_required",
        "Please enter points"
      ),
    });
    return;
  }

  try {
    await awardOrgCampaignGiftBonusPoint(cId, Number(teamId), {
      points,
      comment: bonusPointForm.comment || "",
    });

    notificationsMixinMakeToastCompat({
      group: "Notification",
      title: $t(
        "edit_campaign.campaign_gifts_internal_tab_bonus_points_success",
        "Bonus points awarded"
      ),
    });

    bonusPointForm.team = null;
    bonusPointForm.points = "";
    bonusPointForm.comment = "";

    if (campaignGiftBonusPointListPage.value !== 1) {
      campaignGiftBonusPointListPage.value = 1;
    } else {
      refetchCampaignGiftBonusPointList();
    }
  } catch (e) {
    notificationsMixinHandleCatch(e);
  }
}

function onSubmitCampaignGiftModal(hide) {
  hide();
  fetchOrgCampaignGiftList(cId, {
    page: campaignGiftListPage.value,
    per_page: 10,
  });
}

function onSubmitCampaignGiftClaimsModal(hide) {
  hide();
  refetchCampaignGiftClaimList();
}

function onDeleteGift(id) {
  deleteOrgCampaignGift(cId, id)
    .then(() => {
      notificationsMixinMakeToastCompat({
        group: "Notification",
        title: $t("edit_campaign.success_delete_campaign_gift", "Deleted"),
      });
    })
    .catch((e) => notificationsMixinHandleCatch(e))
    .finally(() => {
      fetchOrgCampaignGiftList(cId, {
        page: campaignGiftListPage.value,
        per_page: 10,
      });
    });
}

function onDeleteGiftClaim(id) {
  deleteOrgCampaignGiftClaim(cId, id)
    .then(() => {
      notificationsMixinMakeToastCompat({
        group: "Notification",
        title: $t("edit_campaign.success_delete_campaign_gifts_claim", "Deleted"),
      });
    })
    .catch((e) => notificationsMixinHandleCatch(e))
    .finally(() => {
      refetchCampaignGiftClaimList();
    });
}

const exportLoading = ref(false);

function exportGenericGiftClaims(params = {}) {
  exportLoading.value = true;

  exportCampaignGiftClaimByOrg(cId, params)
    .then((res) => {
      window.open(res.data.tmp_url, "_blank");

      notificationsMixinMakeToastCompat({
        group: "Notification",
        title: $t("edit_campaign.success_export_campaign_gifts_claim", "Exported"),
      });
    })
    .catch((e) => notificationsMixinHandleCatch(e))
    .finally(() => {
      exportLoading.value = false;
    });
}

function exportGiftClaims() {
  exportGenericGiftClaims();
}

function exportGiftClaimsWithRemainingReport() {
  exportGenericGiftClaims({ remaining_report: 1 });
}

// --- Bulk status update for gift claims ---
const selectedClaimIds = ref([]);
const bulkStatus = ref("");
const bulkUpdating = ref(false);

const claimStatusOptions = computed(() => [
  {
    value: "",
    text: $t("edit_campaign.campaign_gifts_claims_bulk_status_placeholder", "Select status"),
    disabled: true,
  },
  {
    value: CharidyCampaignGiftsClaimStatusProcessed,
    text: $t(
      "edit_campaign.campaign_gift_claims_modal_status_processed",
      CharidyCampaignGiftsClaimStatusProcessed
    ),
  },
  {
    value: CharidyCampaignGiftsClaimStatusSentToCarrier,
    text: $t(
      "edit_campaign.campaign_gift_claims_modal_status_sent_to_carrier",
      CharidyCampaignGiftsClaimStatusSentToCarrier
    ),
  },
  {
    value: CharidyCampaignGiftsClaimStatusCurrentlyShipping,
    text: $t(
      "edit_campaign.campaign_gift_claims_modal_status_currently_shipping",
      CharidyCampaignGiftsClaimStatusCurrentlyShipping
    ),
  },
  {
    value: CharidyCampaignGiftsClaimStatusDelivered,
    text: $t(
      "edit_campaign.campaign_gift_claims_modal_status_delivered",
      CharidyCampaignGiftsClaimStatusDelivered
    ),
  },
  {
    value: CharidyCampaignGiftsClaimStatusCanceled,
    text: $t(
      "edit_campaign.campaign_gift_claims_modal_status_canceled",
      CharidyCampaignGiftsClaimStatusCanceled
    ),
  },
  {
    value: CharidyCampaignGiftsClaimStatusOther,
    text: $t(
      "edit_campaign.campaign_gift_claims_modal_status_other",
      CharidyCampaignGiftsClaimStatusOther
    ),
  },
]);

// Same statuses as the bulk-update select, but with a non-disabled "All" entry
// so the filter can be cleared.
const claimFilterStatusOptions = computed(() => [
  {
    value: "",
    text: $t("edit_campaign.campaign_gifts_claims_filter_status_all", "All statuses"),
  },
  ...claimStatusOptions.value.slice(1),
]);

const visibleSelectedCount = computed(
  () => campaignGiftsClaimList.value.filter((c) => selectedClaimIds.value.includes(c.ID)).length
);

const allClaimsSelected = computed(
  () =>
    campaignGiftsClaimList.value.length > 0 &&
    visibleSelectedCount.value === campaignGiftsClaimList.value.length
);

const someClaimsSelected = computed(
  () => visibleSelectedCount.value > 0 && !allClaimsSelected.value
);

function isClaimSelected(id) {
  return selectedClaimIds.value.includes(id);
}

function toggleClaim(id, checked) {
  if (checked) {
    if (!selectedClaimIds.value.includes(id)) {
      selectedClaimIds.value = [...selectedClaimIds.value, id];
    }
  } else {
    selectedClaimIds.value = selectedClaimIds.value.filter((cid) => cid !== id);
  }
}

function toggleSelectAllClaims(checked) {
  if (checked) {
    selectedClaimIds.value = campaignGiftsClaimList.value.map((c) => c.ID);
  } else {
    selectedClaimIds.value = [];
  }
}

async function onBulkUpdateClaimStatus() {
  if (!selectedClaimIds.value.length) {
    notificationsMixinMakeToastCompat({
      group: "ErrorNotification",
      title: $t(
        "edit_campaign.campaign_gifts_claims_bulk_no_selection",
        "Please select at least one gift claim"
      ),
    });
    return;
  }

  if (!bulkStatus.value) {
    notificationsMixinMakeToastCompat({
      group: "ErrorNotification",
      title: $t("edit_campaign.campaign_gifts_claims_bulk_no_status", "Please select a status"),
    });
    return;
  }

  bulkUpdating.value = true;

  try {
    await bulkUpdateOrgCampaignGiftClaimStatus(cId, selectedClaimIds.value, bulkStatus.value);

    notificationsMixinMakeToastCompat({
      group: "Notification",
      title: $t("edit_campaign.campaign_gifts_claims_bulk_success", "Statuses updated"),
    });

    selectedClaimIds.value = [];
    bulkStatus.value = "";

    refetchCampaignGiftClaimList();
  } catch (e) {
    notificationsMixinHandleCatch(e);
  } finally {
    bulkUpdating.value = false;
  }
}

const campaignGiftListFields = computed(() => {
  return [
    {
      key: "ID",
      label: $t("edit_campaign.campaign_gifts_internal_table_gift_id", "ID"),
    },
    {
      key: "campaign_id",
      label: $t("edit_campaign.campaign_gifts_internal_table_campaign_id", "Campaign ID"),
    },
    {
      key: "title",
      label: $t("edit_campaign.campaign_gifts_internal_table_gift_title", "Title"),
    },
    {
      key: "image",
      label: $t("edit_campaign.campaign_gifts_internal_table_gift_image", "Image"),
    },
    {
      key: "quantity",
      label: $t("edit_campaign.campaign_gifts_internal_table_gift_quantity", "Quantity"),
    },
    {
      key: "points",
      label: $t("edit_campaign.campaign_gifts_internal_table_gift_points", "Points"),
    },
    {
      key: "order",
      label: $t("edit_campaign.campaign_gifts_internal_table_gift_order", "Order"),
    },
    {
      key: "scope",
      label: $t("edit_campaign.campaign_gifts_internal_table_gift_scope", "Scope"),
    },
    {
      key: "actions",
      label: $t("edit_campaign.campaign_gifts_internal_table_gift_actions", "Actions"),
    },
  ];
});

const campaignGiftsClaimListFields = computed(() => {
  return [
    {
      key: "selected",
      label: "",
    },
    {
      key: "ID",
      label: $t("edit_campaign.campaign_gifts_internal_table_claim_id", "ID"),
    },
    {
      key: "gift",
      label: $t("edit_campaign.campaign_gifts_internal_table_claim_gift", "Gift"),
    },
    {
      key: "team",
      label: $t("edit_campaign.campaign_gifts_internal_table_claim_team", "Team"),
    },
    {
      key: "points",
      label: $t("edit_campaign.campaign_gifts_internal_table_claim_points", "Points"),
    },
    // {
    //   key: "quantity",
    //   label: $t("edit_campaign.campaign_gifts_internal_table_claim_quantity", "Quantity"),
    // },
    {
      key: "status",
      label: $t("edit_campaign.campaign_gifts_internal_table_claim_status", "Status"),
    },
    {
      key: "actions",
      label: $t("edit_campaign.campaign_gifts_internal_table_claim_actions", "Actions"),
    },
  ];
});

const campaignGiftBonusPointListFields = computed(() => {
  return [
    {
      key: "ID",
      label: $t("edit_campaign.campaign_gifts_internal_table_bonus_point_id", "ID"),
    },
    {
      key: "team_id",
      label: $t("edit_campaign.campaign_gifts_internal_table_bonus_point_team", "Team ID"),
    },
    {
      key: "points",
      label: $t("edit_campaign.campaign_gifts_internal_table_bonus_point_points", "Points"),
    },
    {
      key: "comment",
      label: $t("edit_campaign.campaign_gifts_internal_table_bonus_point_comment", "Comment"),
    },
    {
      key: "actions",
      label: $t("edit_campaign.campaign_gifts_internal_table_bonus_point_actions", "Actions"),
    },
  ];
});

const statusVariant = (status) => {
  switch (status) {
    case CharidyCampaignGiftsClaimStatusSentToCarrier:
      return "warning";
    case CharidyCampaignGiftsClaimStatusCurrentlyShipping:
      return "info";
    case CharidyCampaignGiftsClaimStatusDelivered:
      return "success";
    case CharidyCampaignGiftsClaimStatusOther:
      return "secondary";
    case CharidyCampaignGiftsClaimStatusProcessed:
      return "info";
    case CharidyCampaignGiftsClaimStatusCanceled:
      return "danger";
    default:
      return "secondary";
  }
};

const teamName = computed(() => (item) => {
  if (!item.team) {
    return item.team_id;
  }

  return `${item.team?.name} - ${item.team_id}`;
});

const getTeamLink = computed(() => (item) => {
  if (!item.team || !item.campaign) {
    return "";
  }

  return `${appHost}/${item.campaign.short_link}/${item.team.slug}`;
});

function formatScope(scope) {
  if (!scope) return "all";
  try {
    const parsed = JSON.parse(scope);
    if (Array.isArray(parsed)) {
      return parsed.join(", ");
    }
  } catch {
    // Legacy single-value format
  }
  return scope;
}

const giftDetail = computed(() => (item) => {
  // add line break to gift title every 4 words to avoid overflow
  const addBreakLines = (text) =>
    text
      .split(" ")
      .map((word, i) => (i > 0 && i % 4 === 0 ? "<br>" : "") + word)
      .join(" ");

  const giftName = item.gift?.title ? addBreakLines(item.gift.title) : item.gift_id;
  const giftImage = item.gift?.image || "";

  return {
    name: giftName,
    image: giftImage,
  };
});
</script>

<template>
  <div>
    <b-card class="!tw-border-0" no-body>
      <b-tabs pills card vertical>
        <b-tab
          :title="$t('edit_campaign.campaign_gifts_internal_tab_title_settings', 'Settings')"
          active
        >
          <Setting title="" name="chinese_auction" :scheme="ChineseAuctionSchema($t)" />
        </b-tab>
        <b-tab :title="$t('edit_campaign.campaign_gifts_internal_tab_title_gifts', 'Gifts')">
          <div class="tw-flex tw-items-center tw-gap-3 tw-mb-5">
            <CampaignGiftModal @submit="onSubmitCampaignGiftModal" v-slot="{ show, loading }">
              <button
                @click="() => show()"
                type="button"
                :disabled="loading"
                :class="{
                  disabled: loading,
                }"
                class="bttn bttn--lg bttn--orange"
              >
                {{ $t("edit_campaign.campaign_gifts_internal_tab_action_create_gift", "Create") }}
              </button>
            </CampaignGiftModal>
          </div>
          <b-table
            class="!tw-mb-5"
            id="campaign-gift-list"
            :busy="loadingCampaignGift"
            :items="campaignGiftList"
            :fields="campaignGiftListFields"
          >
            <template #cell(image)="{ item }">
              <div class="tw-flex tw-items-center tw-gap-3">
                <img
                  v-if="item.image"
                  :src="item.image"
                  :alt="item.title"
                  width="140"
                  height="140"
                  class="tw-w-[140px] tw-h-[140px] tw-rounded-full tw-object-cover tw-mb-3 lg:tw-mb-0"
                />
                <div
                  v-else
                  aria-roledescription="image placeholder"
                  class="tw-w-[140px] tw-h-[140px] tw-rounded-full tw-bg-gray-300 tw-mb-3 lg:tw-mb-0"
                />
              </div>
            </template>
            <template #cell(scope)="{ item }">
              <span>{{ formatScope(item.scope) }}</span>
            </template>
            <template #cell(actions)="{ item }">
              <div class="tw-flex tw-items-center tw-gap-3">
                <CampaignGiftModal @submit="onSubmitCampaignGiftModal" v-slot="{ show, loading }">
                  <button
                    :disabled="loading"
                    :aria-label="
                      $t('edit_campaign.campaign_gifts_internal_tab_action_edit_gift', 'Edit')
                    "
                    class="bttn bttn-round"
                    @click="show(item.ID)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                      />
                      <path d="M0 0h24v24H0z" fill="none" />
                    </svg>
                  </button>
                </CampaignGiftModal>
                <MsgBoxConfirm
                  as="button"
                  :disabled="loadingCampaignGift"
                  :aria-label="
                    $t('edit_campaign.campaign_gifts_internal_tab_action_delete_gift', 'Delete')
                  "
                  :title="$t('edit_campaign.confirm_delete_campaign_gift', 'Delete campaign gift?')"
                  class="bttn bttn-round"
                  @confirm="onDeleteGift(item.ID)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
                    />
                    <path d="M0 0h24v24H0z" fill="none" />
                  </svg>
                </MsgBoxConfirm>
              </div>
            </template>
          </b-table>
          <b-pagination
            v-model="campaignGiftListPage"
            :total-rows="campaignGiftListTotal"
            :per-page="10"
            aria-controls="campaign-gift-list"
          ></b-pagination>
        </b-tab>
        <b-tab
          :title="$t('edit_campaign.campaign_gifts_internal_tab_title_gift_claims', 'Gift claims')"
        >
          <div class="tw-flex tw-items-center tw-gap-3 tw-mb-5">
            <button
              type="button"
              class="bttn bttn--lg bttn--orange"
              :disabled="exportLoading"
              @click="exportGiftClaims"
            >
              <span
                v-if="exportLoading"
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              <span v-else>
                {{ $t("edit_campaign.campaign_gifts_internal_tab_action_export_claims", "Export") }}
              </span>
            </button>

            <button
              type="button"
              class="bttn bttn--lg bttn--orange"
              :disabled="exportLoading"
              @click="exportGiftClaimsWithRemainingReport"
            >
              <span
                v-if="exportLoading"
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              <span v-else>
                {{
                  $t(
                    "edit_campaign.campaign_gifts_internal_tab_action_export_claims_remaining_report",
                    "Export with remaining points report"
                  )
                }}
              </span>
            </button>
          </div>
          <div
            class="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-mb-5 tw-p-3 tw-rounded tw-bg-gray-100"
          >
            <span class="tw-font-bold">
              {{ $t("edit_campaign.campaign_gifts_claims_bulk_selected", "Selected") }}:
              {{ selectedClaimIds.length }}
            </span>
            <b-form-select
              v-model="bulkStatus"
              :options="claimStatusOptions"
              :disabled="bulkUpdating || !selectedClaimIds.length"
              class="tw-max-w-[260px]"
            ></b-form-select>
            <button
              type="button"
              class="bttn bttn--lg bttn--orange"
              :disabled="bulkUpdating || !selectedClaimIds.length || !bulkStatus"
              @click="onBulkUpdateClaimStatus"
            >
              <span
                v-if="bulkUpdating"
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              <span v-else>
                {{
                  $t("edit_campaign.campaign_gifts_claims_bulk_apply", "Update status of selected")
                }}
              </span>
            </button>
          </div>
          <div
            class="tw-flex tw-flex-wrap tw-items-end tw-gap-3 tw-mb-5 tw-p-3 tw-rounded tw-bg-gray-100"
          >
            <b-form-group
              :label="$t('edit_campaign.campaign_gifts_claims_filter_status_label', 'Status')"
              class="tw-mb-0"
            >
              <b-form-select
                v-model="claimFilterStatus"
                :options="claimFilterStatusOptions"
                class="tw-max-w-[220px]"
              ></b-form-select>
            </b-form-group>
            <b-form-group
              :label="$t('edit_campaign.campaign_gifts_claims_filter_gift_name_label', 'Gift name')"
              class="tw-mb-0"
            >
              <b-form-input
                v-model="claimFilterGiftName"
                :placeholder="
                  $t(
                    'edit_campaign.campaign_gifts_claims_filter_gift_name_placeholder',
                    'Search gift name'
                  )
                "
                @keyup.enter="applyClaimFilters"
              />
            </b-form-group>
            <b-form-group
              :label="$t('edit_campaign.campaign_gifts_claims_filter_team_name_label', 'Team name')"
              class="tw-mb-0"
            >
              <b-form-input
                v-model="claimFilterTeamName"
                :placeholder="
                  $t(
                    'edit_campaign.campaign_gifts_claims_filter_team_name_placeholder',
                    'Search team name'
                  )
                "
                @keyup.enter="applyClaimFilters"
              />
            </b-form-group>
            <b-form-group
              :label="$t('edit_campaign.campaign_gifts_claims_filter_points_label', 'Points')"
              class="tw-mb-0"
            >
              <b-form-input
                v-model="claimFilterPoints"
                type="number"
                step="any"
                min="0"
                :placeholder="
                  $t(
                    'edit_campaign.campaign_gifts_claims_filter_points_placeholder',
                    'Exact points'
                  )
                "
                @keyup.enter="applyClaimFilters"
              />
            </b-form-group>
            <!-- Invisible label keeps the buttons aligned on the same row as the labeled inputs -->
            <b-form-group label-class="tw-invisible" label="actions" class="tw-mb-0">
              <div class="tw-flex tw-items-center tw-gap-2">
                <button type="button" class="bttn bttn--lg bttn--orange" @click="applyClaimFilters">
                  {{ $t("edit_campaign.campaign_gifts_claims_filter_apply", "Apply filters") }}
                </button>
                <button
                  type="button"
                  class="bttn bttn--lg bttn--bordered-blue"
                  @click="resetClaimFilters"
                >
                  {{ $t("edit_campaign.campaign_gifts_claims_filter_reset", "Reset") }}
                </button>
              </div>
            </b-form-group>
          </div>
          <b-table
            id="campaign-gift-claims-table"
            class="!tw-mb-5"
            :busy="loadingCampaignGiftsClaim"
            :items="campaignGiftsClaimList"
            :fields="campaignGiftsClaimListFields"
          >
            <template #head(selected)>
              <b-form-checkbox
                :checked="allClaimsSelected"
                :indeterminate="someClaimsSelected"
                :disabled="!campaignGiftsClaimList.length"
                @change="toggleSelectAllClaims"
              />
            </template>
            <template #cell(selected)="{ item }">
              <b-form-checkbox
                :checked="isClaimSelected(item.ID)"
                @change="(checked) => toggleClaim(item.ID, checked)"
              />
            </template>
            <template #cell(gift)="{ item }">
              <div class="tw-flex tw-flex-col tw-items-center tw-gap-3">
                <img
                  v-if="giftDetail(item).image"
                  :src="giftDetail(item).image"
                  :alt="giftDetail(item).name"
                  width="140"
                  height="140"
                  class="tw-w-[140px] tw-h-[140px] tw-rounded-full tw-object-cover tw-mb-3 lg:tw-mb-0"
                />
                <p class="tw-text-lg tw-font-bold" v-html="giftDetail(item).name"></p>
              </div>
            </template>
            <template #cell(status)="{ item }">
              <b-alert show :variant="statusVariant(item.status)">
                <span v-if="item.status === CharidyCampaignGiftsClaimStatusSentToCarrier">
                  {{
                    $t(
                      "edit_campaign.campaign_gifts_claims_modal_status_sent_to_carrier",
                      CharidyCampaignGiftsClaimStatusSentToCarrier
                    )
                  }}
                </span>
                <span v-else-if="item.status === CharidyCampaignGiftsClaimStatusCurrentlyShipping">
                  {{
                    $t(
                      "edit_campaign.campaign_gifts_claims_modal_status_currently_shipping",
                      CharidyCampaignGiftsClaimStatusCurrentlyShipping
                    )
                  }}
                </span>
                <span v-else-if="item.status === CharidyCampaignGiftsClaimStatusDelivered">
                  {{
                    $t(
                      "edit_campaign.campaign_gifts_claims_modal_status_delivered",
                      CharidyCampaignGiftsClaimStatusDelivered
                    )
                  }}
                </span>
                <span v-else-if="item.status === CharidyCampaignGiftsClaimStatusOther">
                  {{
                    $t(
                      "edit_campaign.campaign_gifts_claims_modal_status_other",
                      CharidyCampaignGiftsClaimStatusOther
                    )
                  }}
                </span>
                <span v-else-if="item.status === CharidyCampaignGiftsClaimStatusProcessed">
                  {{
                    $t(
                      "edit_campaign.campaign_gifts_claims_modal_status_processed",
                      CharidyCampaignGiftsClaimStatusProcessed
                    )
                  }}
                </span>
                <span v-else-if="item.status === CharidyCampaignGiftsClaimStatusCanceled">
                  {{
                    $t(
                      "edit_campaign.campaign_gifts_claims_modal_status_canceled",
                      CharidyCampaignGiftsClaimStatusCanceled
                    )
                  }}
                </span>
                <span v-else>
                  {{ $t("edit_campaign.campaign_gifts_claims_modal_status_undefined", "None") }}
                </span>
              </b-alert>
            </template>
            <template #cell(team)="{ item }">
              <a :href="getTeamLink(item)" v-if="item.team_id">{{ teamName(item) }}</a>
              <span v-else>{{
                $t("edit_campaign.campaign_gifts_claims_modal_team_id_none", "None")
              }}</span>
            </template>
            <template #cell(actions)="{ item }">
              <div class="tw-flex tw-items-center tw-gap-3">
                <CampaignGiftsClaimModal
                  @submit="onSubmitCampaignGiftClaimsModal"
                  v-slot="{ show, loading }"
                >
                  <button
                    :disabled="loading"
                    :aria-label="
                      $t('edit_campaign.campaign_gifts_internal_tab_action_edit_gift', 'Edit')
                    "
                    class="bttn bttn-round"
                    @click="show(item.ID)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                      />
                      <path d="M0 0h24v24H0z" fill="none" />
                    </svg>
                  </button>
                </CampaignGiftsClaimModal>
                <MsgBoxConfirm
                  as="button"
                  :disabled="loadingCampaignGiftsClaim"
                  :aria-label="
                    $t('edit_campaign.campaign_gifts_internal_tab_action_delete_gift', 'Delete')
                  "
                  :title="
                    $t(
                      'edit_campaign.confirm_delete_campaign_gifts_claim',
                      'Delete campaign gifts claim?'
                    )
                  "
                  class="bttn bttn-round"
                  @confirm="onDeleteGiftClaim(item.ID)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
                    />
                    <path d="M0 0h24v24H0z" fill="none" />
                  </svg>
                </MsgBoxConfirm>
              </div>
            </template>
          </b-table>
          <b-pagination
            v-model="campaignGiftsClaimListPage"
            :total-rows="campaignGiftsClaimListTotal"
            :per-page="10"
            aria-controls="campaign-gift-claims-table"
          ></b-pagination>
        </b-tab>
        <b-tab
          :title="
            $t('edit_campaign.campaign_gifts_internal_tab_title_bonus_points', 'Bonus points')
          "
        >
          <form
            class="tw-flex tw-flex-col tw-gap-3 tw-mb-5"
            @submit.prevent="onSubmitAwardBonusPoint"
          >
            <b-form-group
              :label="
                $t('edit_campaign.campaign_gifts_internal_tab_bonus_points_team_label', 'Team')
              "
            >
              <MultiselectSearch
                v-model="bonusPointForm.team"
                :open-prefetch="true"
                :search-url="teamSearchUrl"
                :search-options="teamSearchOptions"
                :multiple="false"
                :close-on-select="true"
              >
                <template #no-options>
                  {{ $t("dashboard.team_search_no_options", "Please enter 1 or more characters") }}
                </template>
                <template #no-more-infinite>
                  {{ $t("dashboard.team_search_no_more", "No more teams") }}
                </template>
              </MultiselectSearch>
            </b-form-group>
            <b-form-group
              :label="
                $t('edit_campaign.campaign_gifts_internal_tab_bonus_points_points_label', 'Points')
              "
            >
              <b-form-input
                v-model="bonusPointForm.points"
                type="number"
                step="any"
                min="0"
                required
              />
            </b-form-group>
            <b-form-group
              :label="
                $t(
                  'edit_campaign.campaign_gifts_internal_tab_bonus_points_comment_label',
                  'Comment'
                )
              "
            >
              <b-form-textarea v-model="bonusPointForm.comment" rows="2" />
            </b-form-group>
            <div>
              <button
                type="submit"
                class="bttn bttn--lg bttn--orange"
                :disabled="submittingCampaignGiftBonusPoint"
              >
                <span
                  v-if="submittingCampaignGiftBonusPoint"
                  class="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span v-else>
                  {{
                    $t(
                      "edit_campaign.campaign_gifts_internal_tab_bonus_points_submit",
                      "Award bonus points"
                    )
                  }}
                </span>
              </button>
            </div>
          </form>

          <hr />

          <b-form-group
            :label="
              $t(
                'edit_campaign.campaign_gifts_internal_tab_bonus_points_filter_team_label',
                'Filter by team'
              )
            "
            class="tw-mb-5"
          >
            <MultiselectSearch
              v-model="campaignGiftBonusPointFilterTeam"
              :open-prefetch="true"
              :search-url="teamSearchUrl"
              :search-options="teamSearchOptions"
              :multiple="false"
              :close-on-select="true"
            >
              <template #no-options>
                {{ $t("dashboard.team_search_no_options", "Please enter 1 or more characters") }}
              </template>
              <template #no-more-infinite>
                {{ $t("dashboard.team_search_no_more", "No more teams") }}
              </template>
            </MultiselectSearch>
          </b-form-group>

          <b-table
            id="campaign-gift-bonus-point-table"
            class="!tw-mb-5"
            :busy="loadingCampaignGiftBonusPoint"
            :items="campaignGiftBonusPointList"
            :fields="campaignGiftBonusPointListFields"
          >
            <template #cell(actions)="{ item }">
              <div class="tw-flex tw-items-center tw-gap-3">
                <button
                  type="button"
                  :disabled="submittingCampaignGiftBonusPoint"
                  :aria-label="
                    $t('edit_campaign.campaign_gifts_internal_tab_action_edit_gift', 'Edit')
                  "
                  class="bttn bttn-round"
                  @click="openEditBonusPoint(item)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                    />
                    <path d="M0 0h24v24H0z" fill="none" />
                  </svg>
                </button>
                <MsgBoxConfirm
                  as="button"
                  :disabled="submittingCampaignGiftBonusPoint"
                  :aria-label="
                    $t('edit_campaign.campaign_gifts_internal_tab_action_delete_gift', 'Delete')
                  "
                  :title="
                    $t(
                      'edit_campaign.confirm_delete_campaign_gift_bonus_point',
                      'Delete bonus points entry?'
                    )
                  "
                  class="bttn bttn-round"
                  @confirm="onDeleteBonusPoint(item.ID)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
                    />
                    <path d="M0 0h24v24H0z" fill="none" />
                  </svg>
                </MsgBoxConfirm>
              </div>
            </template>
          </b-table>
          <b-pagination
            v-model="campaignGiftBonusPointListPage"
            :total-rows="campaignGiftBonusPointListTotal"
            :per-page="10"
            aria-controls="campaign-gift-bonus-point-table"
          ></b-pagination>
        </b-tab>
      </b-tabs>
    </b-card>

    <b-modal
      v-model="bonusPointEditModalVisible"
      :title="
        $t('edit_campaign.campaign_gifts_internal_tab_bonus_points_edit_title', 'Edit bonus points')
      "
      hide-footer
    >
      <form class="tw-flex tw-flex-col tw-gap-3" @submit.prevent="onSubmitEditBonusPoint">
        <b-form-group
          :label="
            $t('edit_campaign.campaign_gifts_internal_tab_bonus_points_points_label', 'Points')
          "
        >
          <b-form-input
            v-model="bonusPointEditForm.points"
            type="number"
            step="any"
            min="0"
            required
          />
        </b-form-group>
        <b-form-group
          :label="
            $t('edit_campaign.campaign_gifts_internal_tab_bonus_points_comment_label', 'Comment')
          "
        >
          <b-form-textarea v-model="bonusPointEditForm.comment" rows="2" />
        </b-form-group>
        <div class="tw-flex tw-items-center tw-gap-3 tw-justify-end">
          <button
            type="button"
            class="bttn bttn--lg bttn--bordered-blue"
            :disabled="submittingCampaignGiftBonusPoint"
            @click="bonusPointEditModalVisible = false"
          >
            {{ $t("edit_campaign.campaign_gifts_internal_tab_action_cancel", "Cancel") }}
          </button>
          <button
            type="submit"
            class="bttn bttn--lg bttn--orange"
            :disabled="submittingCampaignGiftBonusPoint"
          >
            <span
              v-if="submittingCampaignGiftBonusPoint"
              class="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></span>
            <span v-else>
              {{ $t("edit_campaign.campaign_gifts_internal_tab_bonus_points_save", "Save") }}
            </span>
          </button>
        </div>
      </form>
    </b-modal>
  </div>
</template>
